import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { AuthService } from '../services/auth.service';
import { AccountOption, AccountRegistryService } from '../services/account-registry.service';
import { InputOtpModule } from 'primeng/inputotp';
import { FestiveService } from '../../services/festive.service';
import { FestiveDoodleComponent } from '../../../shared/components/festive-doodle/festive-doodle.component';
import { FestiveFireworksComponent } from '../../../shared/components/festive-fireworks/festive-fireworks.component';

type FormMessage = { type: 'error' | 'success'; text: string } | null;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    CheckboxModule,
    InputOtpModule,
    FestiveDoodleComponent,
    FestiveFireworksComponent
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  readonly festiveService = inject(FestiveService);
  loginForm!: FormGroup;
  resetForm!: FormGroup;
  loading = false;
  resetLoading = false;
  submitted = false;
  rememberMe = false;
  /** Password reset view: step 1 = enter the code, step 2 = choose a new password */
  showResetForm = false;
  otpVerified = false;
  /** Inline message shown above the form actions (replaces toasts on this screen) */
  message: FormMessage = null;
  /** Accounts still signed in on this browser (Switch Company), offered instead of a new login */
  savedAccounts: AccountOption[] = [];
  readonly year = new Date().getFullYear();

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private accountRegistry: AccountRegistryService
  ) { }

  ngOnInit() {
    this.initForm();
    this.initResetForm();
    this.checkRememberedCredentials();
    this.savedAccounts = this.accountRegistry.list().filter(account => !account.expired);
    // Switching to an account whose session expired lands here with its username
    const username = new URLSearchParams(window.location.search).get('username');
    if (username) this.loginForm.patchValue({ username });
  }

  continueAs(account: AccountOption) {
    this.accountRegistry.switchTo(account.id);
  }

  initForm() {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]]
    });
  }

  initResetForm() {
    this.resetForm = this.formBuilder.group({
      otp: ['', Validators.required],
      newPassword: [''],
      confirmPassword: ['']
    });
  }

  checkRememberedCredentials() {
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      this.loginForm.patchValue({ username: rememberedUsername });
      this.rememberMe = true;
    }
  }

  fieldInvalid(name: string): boolean {
    const control = this.loginForm.get(name);
    return !!control && control.invalid && (control.dirty || this.submitted);
  }

  /** "Forgot password?" sends a reset code to the username's registered contact */
  forgotPassword() {
    const username = (this.loginForm.value.username || '').trim();
    if (!username) {
      this.loginForm.get('username')?.markAsDirty();
      this.message = { type: 'error', text: 'Enter your username first, then choose "Forgot password?".' };
      return;
    }
    this.message = null;
    this.resetLoading = true;
    this.authService.sendOTP(username).subscribe({
      next: () => {
        this.resetLoading = false;
        this.resetForm.reset();
        this.otpVerified = false;
        this.showResetForm = true;
        this.message = { type: 'success', text: 'We sent a 6-digit code to your registered contact.' };
      },
      error: (err) => {
        this.resetLoading = false;
        this.message = { type: 'error', text: err.error?.message || 'Could not send the reset code.' };
      }
    });
  }

  backToSignIn() {
    this.showResetForm = false;
    this.otpVerified = false;
    this.message = null;
  }

  onResetSubmit() {
    if (this.resetForm.invalid) return;
    const username = this.loginForm.value.username;
    this.resetLoading = true;

    if (!this.otpVerified) {
      this.authService.verifyOTP(username, this.resetForm.value.otp).subscribe({
        next: () => {
          this.resetLoading = false;
          this.otpVerified = true;
          this.message = { type: 'success', text: 'Code verified. Choose a new password.' };
          this.resetForm.get('newPassword')?.setValidators(Validators.required);
          this.resetForm.get('confirmPassword')?.setValidators(Validators.required);
          this.resetForm.get('newPassword')?.updateValueAndValidity();
          this.resetForm.get('confirmPassword')?.updateValueAndValidity();
        },
        error: (err) => {
          this.resetLoading = false;
          this.message = { type: 'error', text: err.error?.message || 'Invalid code or username.' };
        }
      });
      return;
    }

    const { otp, newPassword, confirmPassword } = this.resetForm.value;
    if (newPassword !== confirmPassword) {
      this.resetLoading = false;
      this.message = { type: 'error', text: 'Passwords do not match.' };
      return;
    }

    this.authService.resetPassword({ username, otp, newPassword }).subscribe({
      next: () => {
        this.resetLoading = false;
        this.showResetForm = false;
        this.otpVerified = false;
        this.loginForm.patchValue({ password: '' });
        this.message = { type: 'success', text: 'Password updated. Sign in with your new password.' };
      },
      error: (err) => {
        this.resetLoading = false;
        this.message = { type: 'error', text: err.error?.message || 'Password reset failed.' };
      }
    });
  }

  onSubmit() {
    this.submitted = true;
    if (this.loginForm.invalid) {
      this.message = { type: 'error', text: 'Enter your username and password.' };
      return;
    }

    this.message = null;
    this.loading = true;
    const { username, password } = this.loginForm.value;

    if (this.rememberMe) {
      localStorage.setItem('rememberedUsername', username);
    } else {
      localStorage.removeItem('rememberedUsername');
    }

    // AuthService navigates to the dashboard on success
    this.authService.login(username, password).subscribe({
      next: (response) => {
        this.loading = false;
        if (!(response && response.status === 'Success' && response.accessToken)) {
          this.message = { type: 'error', text: 'Unexpected response from the server. Please try again.' };
          return;
        }
        // The dashboard shell plays the festival fireworks once
        this.festiveService.markSignedIn();
      },
      error: (error) => {
        this.loading = false;
        let text = 'Sign in failed. Please try again.';
        if (error.status === 401) {
          text = error.error?.message || 'Incorrect username or password.';
        } else if (error.error?.message) {
          text = error.error.message;
        } else if (error.status === 0) {
          text = 'Cannot reach the server. Check your connection.';
        }
        this.message = { type: 'error', text };
      }
    });
  }
}
